import { useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BarChart3,
  FileSpreadsheet,
  FileText,
  Loader2,
} from "lucide-react";
import type {
  CurrencyCode,
  EventSummary,
  Locale,
  ReportRange,
  SalesGroupBy,
  SalesReportQuery,
  SalesReportRow,
  SalesReportTotals,
} from "@ticketnjoy/types";

import { DashboardShell } from "@/components/dashboard-shell";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QueryError } from "@/components/query-error";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuthenticatedMe } from "@/features/organizers/api";
import { useEventsQuery } from "@/features/events/api";
import {
  buildSalesParams,
  filenameFromHeader,
  useSalesQuery,
} from "@/features/reports/api";
import {
  formatIsoDay,
  formatIsoRange,
  istanbulTodayIso,
  REPORT_RANGES,
  useReportRange,
} from "@/features/reports/range";
import { API_BASE_URL } from "@/lib/api-client";
import { usePaymentProviderLabel } from "@/lib/enum-labels";
import { useFormat } from "@/lib/format";
import { useT } from "@/lib/i18n";

const GROUP_BY_OPTIONS: readonly SalesGroupBy[] = ["Day", "Event", "TicketType", "PaymentMethod"];

export function ReportsPage() {
  const me = useAuthenticatedMe();
  const events = useEventsQuery();
  const t = useT("reports");
  const tRange = useT("range");
  const fmt = useFormat();

  const period = useReportRange("month");
  const [shopId, setShopId] = useState<string | null>(null);
  const [eventId, setEventId] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<SalesGroupBy>("Day");

  const query: SalesReportQuery = {
    ...period.query,
    shopId,
    eventId,
    ticketTypeId: null,
    groupBy,
  };

  const sales = useSalesQuery(query, period.valid);
  const rangeLabel =
    period.range === "custom" ? formatIsoRange(fmt, period.from, period.to) : tRange(period.range);

  async function handleExport(format: "xlsx" | "pdf") {
    const failedStatus = await downloadExport(format, query, fmt.locale);
    if (failedStatus !== null) toast.error(t("downloadFailed", { status: failedStatus }));
  }

  if (me.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!me.data) return null;
  if (me.data.role === "Gate") return <Navigate to="/check-in" replace />;
  // SalesReport policy backend tarafında zorlanır; UI guard ek katman.
  if (!["Owner", "Manager", "Accountant"].includes(me.data.role)) {
    return <Navigate to="/forbidden" replace />;
  }

  const shops = me.data.shops;
  const eventsList: EventSummary[] = events.data ?? [];

  return (
    <DashboardShell companyName={me.data.companyName} role={me.data.role}>
      <div className="mb-6">
        <div className="flex items-start gap-3">
          <BarChart3 className="mt-1 h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-semibold">{t("title")}</h1>
            <p className="text-sm text-muted-foreground">{t("description")}</p>
          </div>
        </div>
      </div>

      {/* Filtre paneli */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">{t("filtersTitle")}</CardTitle>
          <CardDescription>{t("filtersDescription")}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Preset / custom range */}
          <div className="space-y-2">
            <Label htmlFor="report-range">{t("rangeLabel")}</Label>
            <Select value={period.range} onValueChange={(v) => period.setRange(v as ReportRange)}>
              <SelectTrigger id="report-range">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REPORT_RANGES.map((p) => (
                  <SelectItem key={p} value={p}>
                    {tRange(p)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {period.range === "custom" ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="report-from">{t("from")}</Label>
                <Input
                  id="report-from"
                  type="date"
                  value={period.from}
                  max={period.to || istanbulTodayIso()}
                  onChange={(e) => period.setFrom(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="report-to">{t("to")}</Label>
                <Input
                  id="report-to"
                  type="date"
                  value={period.to}
                  min={period.from}
                  max={istanbulTodayIso()}
                  onChange={(e) => period.setTo(e.target.value)}
                />
              </div>
            </>
          ) : null}

          {shops.length > 1 ? (
            <div className="space-y-2">
              <Label htmlFor="report-shop">{t("shop")}</Label>
              <Select
                value={shopId ?? "all"}
                onValueChange={(v) => setShopId(v === "all" ? null : v)}
              >
                <SelectTrigger id="report-shop">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("all")}</SelectItem>
                  {shops.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="report-event">{t("event")}</Label>
            <Select
              value={eventId ?? "all"}
              onValueChange={(v) => setEventId(v === "all" ? null : v)}
            >
              <SelectTrigger id="report-event">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("all")}</SelectItem>
                {eventsList.map((ev) => (
                  <SelectItem key={ev.id} value={ev.id}>
                    {ev.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="report-group">{t("groupBy")}</Label>
            <Select value={groupBy} onValueChange={(v) => setGroupBy(v as SalesGroupBy)}>
              <SelectTrigger id="report-group">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GROUP_BY_OPTIONS.map((g) => (
                  <SelectItem key={g} value={g}>
                    {t(`groupByOptions.${g}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Sonuç bloğu */}
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-base">{t("resultsTitle")}</CardTitle>
            <CardDescription>
              {rangeLabel} · {t("rows", { count: sales.data?.rows.length ?? 0 })}
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport("xlsx")}
              disabled={!sales.data || sales.data.rows.length === 0}
            >
              <FileSpreadsheet className="me-2 h-4 w-4" />
              Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport("pdf")}
              disabled={!sales.data || sales.data.rows.length === 0}
            >
              <FileText className="me-2 h-4 w-4" />
              PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {sales.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : sales.error ? (
            <QueryError
              message={t("loadError")}
              detail={sales.error.message}
              onRetry={() => sales.refetch()}
              retrying={sales.isFetching}
            />
          ) : !sales.data || sales.data.rows.length === 0 ? (
            <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
              {t("empty")}
            </div>
          ) : (
            <SalesTable
              groupBy={sales.data.groupBy}
              rows={sales.data.rows}
              totals={sales.data.totals}
              currency={sales.data.currency}
            />
          )}
        </CardContent>
      </Card>
    </DashboardShell>
  );
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function SalesTable({
  groupBy,
  rows,
  totals,
  currency,
}: {
  groupBy: SalesGroupBy;
  rows: SalesReportRow[];
  totals: SalesReportTotals;
  currency: CurrencyCode;
}) {
  const t = useT("reports.columns");
  const fmt = useFormat();
  const providerLabel = usePaymentProviderLabel();
  const money = (value: number) =>
    fmt.number(value, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  const rowLabel = (r: SalesReportRow) =>
    groupBy === "Day"
      ? formatIsoDay(fmt, r.key)
      : groupBy === "PaymentMethod"
        ? providerLabel(r.sortKey)
        : r.key;

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[800px] text-sm">
        <thead className="border-b text-xs uppercase text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-start font-medium">{t(groupBy)}</th>
            <th className="px-3 py-2 text-end font-medium">{t("orders")}</th>
            <th className="px-3 py-2 text-end font-medium">{t("tickets")}</th>
            <th className="px-3 py-2 text-end font-medium">{t("gross")}</th>
            <th className="px-3 py-2 text-end font-medium">{t("tax")}</th>
            <th className="px-3 py-2 text-end font-medium">{t("serviceFee")}</th>
            <th className="px-3 py-2 text-end font-medium">{t("commission")}</th>
            <th className="px-3 py-2 text-end font-medium">{t("net")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.sortKey} className="border-b last:border-0">
              <td className="px-3 py-2 font-medium">{rowLabel(r)}</td>
              <td className="px-3 py-2 text-end tabular-nums">{fmt.number(r.orderCount)}</td>
              <td className="px-3 py-2 text-end tabular-nums">{fmt.number(r.ticketCount)}</td>
              <td className="px-3 py-2 text-end tabular-nums">{money(r.grossRevenue)}</td>
              <td className="px-3 py-2 text-end tabular-nums text-muted-foreground">
                {money(r.taxTotal)}
              </td>
              <td className="px-3 py-2 text-end tabular-nums text-muted-foreground">
                {money(r.serviceFeeTotal)}
              </td>
              <td className="px-3 py-2 text-end tabular-nums text-muted-foreground">
                {money(r.commissionTotal)}
              </td>
              <td className="px-3 py-2 text-end tabular-nums font-semibold text-emerald-600">
                {money(r.organizerNetRevenue)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t-2 bg-muted/30 font-semibold">
          <tr>
            <td className="px-3 py-2 uppercase">{t("total")}</td>
            <td className="px-3 py-2 text-end tabular-nums">{fmt.number(totals.orderCount)}</td>
            <td className="px-3 py-2 text-end tabular-nums">{fmt.number(totals.ticketCount)}</td>
            <td className="px-3 py-2 text-end tabular-nums">{money(totals.grossRevenue)}</td>
            <td className="px-3 py-2 text-end tabular-nums">{money(totals.taxTotal)}</td>
            <td className="px-3 py-2 text-end tabular-nums">{money(totals.serviceFeeTotal)}</td>
            <td className="px-3 py-2 text-end tabular-nums">{money(totals.commissionTotal)}</td>
            <td className="px-3 py-2 text-end tabular-nums text-emerald-700">
              {money(totals.organizerNetRevenue)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Excel/PDF dosyasını authenticated fetch ile çekip browser'da indirir; dosya
 * arayüz dilinde üretilir.
 *
 * Cookie auth (credentials: include) kullanıldığı için `<a href>` direkt link
 * de teorik olarak çalışırdı; ama prod cross-origin senaryoda SameSite cookie
 * davranışı karmaşık. Fetch + Blob + ObjectURL yaklaşımı her ortamda güvenilir.
 */
/** Başarısız olursa HTTP durum kodunu döner; başarıda `null`. */
async function downloadExport(
  format: "xlsx" | "pdf",
  query: SalesReportQuery,
  locale: Locale,
): Promise<number | null> {
  const params = buildSalesParams(query);
  params.set("locale", locale);
  const url = `${API_BASE_URL}/reports/sales/export.${format}?${params.toString()}`;
  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) {
    return response.status;
  }
  const blob = await response.blob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download =
    filenameFromHeader(response) ?? `${locale === "tr" ? "satis-raporu" : "sales-report"}.${format}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
  return null;
}
